import { useCallback, useMemo } from 'react';
import cn from 'classnames';
import { AppId, FCX, usePlatformContext } from '@datassential/platform-ui-lib';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { ConsumerRatingsData, ConsumerRatingsPreset } from '@apiHooks/useHomeConsumerRatings';
import { requestTrialByFeature } from '@actions/page';
import { sanitizeUrl } from '@helpers';
import { ChainType } from '@models/Brands';
import { useHomeWorkspace } from '@pages/Home/context/HomeWorkspaceContext';
import AccessLink from "@components/AccessLink/AccessLink";
import MenuLaunchesAndLTO from './MenuLaunchesAndLTO';
import ConsumerRatingsVsBar from './ConsumerRatingsVsBar';
import ConsumerRatingsNPS from './ConsumerRatingsNPS';
import ConsumerRatingsMegaAttributes from './ConsumerRatingsMegaAttributes';
import ConsumerRatingsStrengthsWeaknesses from './ConsumerRatingsStrengthsWeaknesses';
import './ConsumerRatings.scss';

interface ConsumerRatingsProps {
    data: ConsumerRatingsData;
    selectedPresetId: number | null;
    setSelectedPresetId: (value: number | null) => void;
    isLoading: boolean;
}

const ConsumerRatings: FCX<ConsumerRatingsProps> = ({
    data: consumerRatings,
    selectedPresetId,
    setSelectedPresetId,
    isLoading,
    className
}) => {
    const {
        chainId: dataChainId,
        chainName: dataChainName,
        nps,
        chainType,
        presets,
        megaAttributes,
        strengths,
        weaknesses,
    } = consumerRatings;

    const dispatch = useDispatch();
    const { chainName, chainId } = useHomeWorkspace();
    const { userInfo: { products } } = usePlatformContext();

    const defaultPresetId = consumerRatings.defaultPresetId ?? (presets ?? [])[0]?.id ?? null;
    const finalSelectedPresetId = selectedPresetId ?? defaultPresetId;

    const selectedPreset = useMemo(
        (): ConsumerRatingsPreset | null => (consumerRatings.presets ?? []).find(
            p => p.id === finalSelectedPresetId
        ) ?? null,
        [consumerRatings.presets, finalSelectedPresetId]
    );

    const chainNpsData = useMemo(() => ({
        chainId: dataChainId ?? 0,
        chainName: dataChainName ?? '',
        nps: nps ?? 0,
    }), [dataChainId, dataChainName, nps]);

    const brandPerformance = useMemo(
        () => products[AppId.BrandPerformance],
        [products]
    );
    const fingerprintsLink = brandPerformance?.url ?? '';

    const fingerprintsViewMoreLink = useMemo(() => {
        let typePath = "restaurants";

        switch (chainType) {
            case ChainType.Supermarket: typePath = "grocery"; break;
            case ChainType.CStore: typePath = "c-stores"; break;
        }
        return fingerprintsLink ? sanitizeUrl(`${fingerprintsLink}/${typePath}/chain/${chainId}/overview`) : ""
    }, [chainId, fingerprintsLink, chainType]);

    const handlePresetSelect = useCallback(
        (preset: ConsumerRatingsPreset) => {
            setSelectedPresetId(preset.id ?? null);
        },
        []
    );

    const requestTrial = () => {
        toast.info('Request has been sent');
        dispatch(requestTrialByFeature('brandPerformances'));
    };

    // console.log(isLoading, presets, selectedPreset, finalSelectedPresetId, defaultPresetId, selectedPresetId);
    if (isLoading || !presets?.length || !selectedPreset) return null;

    return (
        <div className={cn(
            'ConsumerRatings',
            className
        )} data-testid="blockConsumerRatings">
            <div className="ConsumerRatings__card">
                <ConsumerRatingsVsBar
                    chainName={chainName}
                    presets={presets}
                    selectedPreset={selectedPreset}
                    onPresetSelect={handlePresetSelect}
                />
                <h2 className="ConsumerRatings__title">Consumer Ratings</h2>
                <ConsumerRatingsNPS currentChain={chainNpsData} selectedPreset={selectedPreset}/>
                <ConsumerRatingsMegaAttributes megaAttributes={megaAttributes ?? []}/>
                <ConsumerRatingsStrengthsWeaknesses
                    strengths={strengths ?? []}
                    weaknesses={weaknesses ?? []}
                    presetName={selectedPreset.name ?? undefined}
                    chainName={chainName}
                />
                <div className="ConsumerRatings__more-link">
                    <AccessLink
                        url={fingerprintsViewMoreLink}
                        text={`More ${chainName} Brand Performance`}
                        altText=""
                        hasAccess={!brandPerformance?.isDisabled}
                        onContactUsClick={requestTrial}
                        testId=""
                    />
                </div>
                <MenuLaunchesAndLTO
                    chainName={chainName}
                    chainId={chainId}
                    comparePresetId={selectedPreset.id ?? 0}
                    comparePresetName={selectedPreset.name ?? ''}
                />
            </div>
        </div>
    );
};

export default ConsumerRatings;
